<template>
  <div class="data-table-container" :class="{ 'fullscreen-mode': isFullscreen }">

    <!-- Header -->
    <DataTableFilters
        :total-filiais="filteredData.length"
        :total-lancamentos="totalLancamentos"
        :is-fullscreen="isFullscreen"
        @toggle-fullscreen="toggleFullscreen"
        @expand-all="expandAll"
        @collapse-all="collapseAll"
    />

    <!-- Tables Card -->
    <v-card class="table-card" elevation="2">
      <v-card-text class="pa-0">
        <div v-if="loading" class="table-loading">
          <v-progress-circular indeterminate color="#023449" size="48"></v-progress-circular>
          <p>Carregando dados...</p>
        </div>

        <div v-else-if="filteredData.length === 0" class="table-empty">
          <v-icon size="64" color="#cbd5e1">mdi-inbox</v-icon>
          <h3>Nenhum registro encontrado</h3>
          <p>Ajuste os filtros ou busca para visualizar dados</p>
        </div>

        <div v-else class="table-wrapper">
          <!-- NÍVEL 1: Filiais -->
          <table class="data-table">
            <thead class="level-1-header">
            <tr>
              <th style="width: 50px;"></th>
              <th class="sortable-header" @click="sortTable('filial')">
                <div class="header-content">
                  <span>Filial</span>
                  <v-icon small :color="isSortActive('filial') ? '#ffffff' : 'rgba(255,255,255,0.5)'">
                    {{ getSortIcon('filial') }}
                  </v-icon>
                </div>
              </th>
              <th class="text-center sortable-header" @click="sortTable('lancamentos')">
                <div class="header-content">
                  <span>Lançamentos</span>
                  <v-icon small :color="isSortActive('lancamentos') ? '#ffffff' : 'rgba(255,255,255,0.5)'">
                    {{ getSortIcon('lancamentos') }}
                  </v-icon>
                </div>
              </th>
              <th class="text-center sortable-header" @click="sortTable('skus')">
                <div class="header-content">
                  <span>SKUs</span>
                  <v-icon small :color="isSortActive('skus') ? '#ffffff' : 'rgba(255,255,255,0.5)'">
                    {{ getSortIcon('skus') }}
                  </v-icon>
                </div>
              </th>
              <th class="text-center sortable-header" @click="sortTable('valorEstoque')">
                <div class="header-content">
                  <span>Valor Estoque</span>
                  <v-icon small :color="isSortActive('valorEstoque') ? '#ffffff' : 'rgba(255,255,255,0.5)'">
                    {{ getSortIcon('valorEstoque') }}
                  </v-icon>
                </div>
              </th>
              <th class="text-center sortable-header" @click="sortTable('valorContado')">
                <div class="header-content">
                  <span>Valor Contado</span>
                  <v-icon small :color="isSortActive('valorContado') ? '#ffffff' : 'rgba(255,255,255,0.5)'">
                    {{ getSortIcon('valorContado') }}
                  </v-icon>
                </div>
              </th>
              <th class="text-center sortable-header" @click="sortTable('valorDivergencia')">
                <div class="header-content">
                  <span>Valor Divergência</span>
                  <v-icon small :color="isSortActive('valorDivergencia') ? '#ffffff' : 'rgba(255,255,255,0.5)'">
                    {{ getSortIcon('valorDivergencia') }}
                  </v-icon>
                </div>
              </th>
              <th class="text-center sortable-header" @click="sortTable('percentualDivergencia')">
                <div class="header-content">
                  <span>% Divergência</span>
                  <v-icon small :color="isSortActive('percentualDivergencia') ? '#ffffff' : 'rgba(255,255,255,0.5)'">
                    {{ getSortIcon('percentualDivergencia') }}
                  </v-icon>
                </div>
              </th>
              <th class="text-center sortable-header" @click="sortTable('status')">
                <div class="header-content">
                  <span>Status</span>
                  <v-icon small :color="isSortActive('status') ? '#ffffff' : 'rgba(255,255,255,0.5)'">
                    {{ getSortIcon('status') }}
                  </v-icon>
                </div>
              </th>
            </tr>
            </thead>
            <tbody>
            <template v-for="filial in paginatedData">
              <!-- Linha da Filial (Nível 1) -->
              <tr
                  :key="`filial-${filial.codigo}`"
                  class="level-1-row"
                  @click="toggleFilial(filial.codigo)"
              >
                <td>
                  <v-btn icon x-small color="#023449">
                    <v-icon small>
                      {{ expandedFiliais.includes(filial.codigo) ? 'mdi-chevron-down' : 'mdi-chevron-right' }}
                    </v-icon>
                  </v-btn>
                </td>
                <td>
                  <div class="cell-content">
                    <v-icon small color="#023449" class="mr-2">mdi-office-building</v-icon>
                    <strong>{{ filial.empresa }}</strong>
                    <span class="filial-code ml-2">(F{{ filial.codigo }})</span>
                  </div>
                </td>
                <td class="text-center">
                  <v-chip small color="#e0f2fe" text-color="#0c4a6e" class="font-weight-bold">
                    {{ filial.quantidadeLancamentosRealizados }} / {{ filial.quantidadeLancamentos }}
                  </v-chip>
                </td>
                <td class="text-center">
                  <v-chip small color="#dbeafe" text-color="#1e40af" class="font-weight-bold">
                    {{ filial.quantidadeSkus }}
                  </v-chip>
                </td>
                <td class="text-center">
                  {{ formatCurrency(filial.valorEstoqueAnterior) }}
                </td>
                <td class="text-center">
                  {{ formatCurrency(filial.valorEstoqueContado) }}
                </td>
                <td class="text-center text-truncate">
                    <span :class="['valor-divergencia', getDivergenciaClass(filial.valorDivergencia)]">
                      {{ formatCurrency(filial.valorDivergencia) }}
                    </span>
                </td>
                <td class="text-center py-2">
                  <v-progress-circular
                      :value="Math.abs(Math.min(filial.percentualDivergencia || 0, 100))"
                      :color="getPercentualColor(filial.percentualDivergencia || 0)"
                      :size="60"
                      :width="2"
                  >
                    <span class="percentual-text">
                      {{
                        Math.abs(filial.percentualDivergencia || 0) >= 100
                            ? (filial.percentualDivergencia || 0).toFixed(0)
                            : (filial.percentualDivergencia || 0).toFixed(2)
                      }}%
                    </span>
                  </v-progress-circular>
                </td>
                <td class="text-center">
                  <v-chip
                      small
                      :color="getStatusColor(filial.status)"
                      :text-color="getStatusTextColor(filial.status)"
                      class="status-chip"
                  >
                    <v-icon left x-small>{{ getStatusIcon(filial.status) }}</v-icon>
                    {{ filial.status }}
                  </v-chip>
                </td>
              </tr>

              <!-- NÍVEL 2: Dias (quando filial expandida) -->
              <template v-if="expandedFiliais.includes(filial.codigo)">
                <tr :key="`dias-header-${filial.codigo}`" class="level-2-header-row">
                  <td colspan="10">
                    <table class="nested-table level-2-table">
                      <thead>
                      <tr>
                        <th style="width: 50px;"></th>
                        <th class="sortable-header" @click="sortTableLevel2(filial.codigo, 'data')">
                          <div class="header-content">
                            <span>Data</span>
                            <v-icon x-small :color="isSortActiveLevel2(filial.codigo, 'data') ? '#023449' : 'rgba(2, 52, 73, 0.5)'">
                              {{ getSortIconLevel2(filial.codigo, 'data') }}
                            </v-icon>
                          </div>
                        </th>
                        <th class="text-center sortable-header" @click="sortTableLevel2(filial.codigo, 'skus')">
                          <div class="header-content">
                            <span>SKUs</span>
                            <v-icon x-small :color="isSortActiveLevel2(filial.codigo, 'skus') ? '#023449' : 'rgba(2, 52, 73, 0.5)'">
                              {{ getSortIconLevel2(filial.codigo, 'skus') }}
                            </v-icon>
                          </div>
                        </th>
                        <th class="text-right sortable-header" @click="sortTableLevel2(filial.codigo, 'valorEstoque')">
                          <div class="header-content">
                            <span>Valor Estoque</span>
                            <v-icon x-small :color="isSortActiveLevel2(filial.codigo, 'valorEstoque') ? '#023449' : 'rgba(2, 52, 73, 0.5)'">
                              {{ getSortIconLevel2(filial.codigo, 'valorEstoque') }}
                            </v-icon>
                          </div>
                        </th>
                        <th class="text-right sortable-header" @click="sortTableLevel2(filial.codigo, 'valorContado')">
                          <div class="header-content">
                            <span>Valor Contado</span>
                            <v-icon x-small :color="isSortActiveLevel2(filial.codigo, 'valorContado') ? '#023449' : 'rgba(2, 52, 73, 0.5)'">
                              {{ getSortIconLevel2(filial.codigo, 'valorContado') }}
                            </v-icon>
                          </div>
                        </th>
                        <th class="text-right sortable-header" @click="sortTableLevel2(filial.codigo, 'valorDivergencia')">
                          <div class="header-content">
                            <span>Divergência</span>
                            <v-icon x-small :color="isSortActiveLevel2(filial.codigo, 'valorDivergencia') ? '#023449' : 'rgba(2, 52, 73, 0.5)'">
                              {{ getSortIconLevel2(filial.codigo, 'valorDivergencia') }}
                            </v-icon>
                          </div>
                        </th>
                        <th class="text-center sortable-header" @click="sortTableLevel2(filial.codigo, 'percentualDivergencia')">
                          <div class="header-content">
                            <span>% Divergência</span>
                            <v-icon x-small :color="isSortActiveLevel2(filial.codigo, 'percentualDivergencia') ? '#023449' : 'rgba(2, 52, 73, 0.5)'">
                              {{ getSortIconLevel2(filial.codigo, 'percentualDivergencia') }}
                            </v-icon>
                          </div>
                        </th>
                      </tr>
                      </thead>
                      <tbody>
                      <template v-for="dia in filial.dias">
                        <!-- Linha do Dia (Nível 2) -->
                        <tr
                            :key="`dia-${filial.codigo}-${dia.data}`"
                            class="level-2-row"
                            @click="toggleDia(filial.codigo, dia.data)"
                        >
                          <td>
                            <v-btn icon x-small color="#023449">
                              <v-icon x-small>
                                {{ isDiaExpanded(filial.codigo, dia.data) ? 'mdi-chevron-down' : 'mdi-chevron-right' }}
                              </v-icon>
                            </v-btn>
                          </td>
                          <td>
                            <div class="cell-content">
                              <v-icon x-small color="#64748b" class="mr-2">mdi-calendar</v-icon>
                              <strong>{{ formatDate(dia.data) }}</strong>
                              <!-- Indicador de muitos produtos -->
                              <v-chip
                                  v-if="dia.produtos && dia.produtos.length > 100"
                                  x-small
                                  color="#fff3cd"
                                  text-color="#856404"
                                  class="ml-2"
                              >
                                <v-icon x-small left>mdi-alert</v-icon>
                                {{ dia.produtos.length }} produtos
                              </v-chip>
                            </div>
                          </td>
                          <td class="text-center">
                            <span class="quantity-badge">{{ dia.quantidadeSkus }}</span>
                          </td>
                          <td class="text-right">{{ formatCurrency(dia.valorEstoque) }}</td>
                          <td class="text-right">{{ formatCurrency(dia.valorContado) }}</td>
                          <td class="text-right text-truncate">
                                <span :class="['valor-divergencia', getDivergenciaClass(dia.valorDivergencia)]">
                                  {{ formatCurrency(dia.valorDivergencia) }}
                                </span>
                          </td>
                          <td class="text-center">
                                <span :class="['percentual-badge', getPercentualBadgeClass(dia.percentualDivergencia || 0)]">
                                  {{ (dia.percentualDivergencia || 0).toFixed(2) }}%
                                </span>
                          </td>
                        </tr>

                        <!-- NÍVEL 3: Produtos (quando dia expandido) -->
                        <template v-if="isDiaExpanded(filial.codigo, dia.data)">
                          <tr :key="`produtos-header-${filial.codigo}-${dia.data}`">
                            <td colspan="8" class="pa-0">
                              <table class="nested-table level-3-table">
                                <thead>
                                <tr>
                                  <th style="width: 50px;"></th>
                                  <th style="width: 120px;" class="sortable-header" @click="sortTableLevel3(filial.codigo, 'codigo')">
                                    <div class="header-content">
                                      <span>Código</span>
                                      <v-icon x-small :color="isSortActiveLevel3(filial.codigo, 'codigo') ? '#023449' : 'rgba(2, 52, 73, 0.5)'">
                                        {{ getSortIconLevel3(filial.codigo, 'codigo') }}
                                      </v-icon>
                                    </div>
                                  </th>
                                  <th class="sortable-header" @click="sortTableLevel3(filial.codigo, 'descricao')">
                                    <div class="header-content">
                                      <span>Descrição</span>
                                      <v-icon x-small :color="isSortActiveLevel3(filial.codigo, 'descricao') ? '#023449' : 'rgba(2, 52, 73, 0.5)'">
                                        {{ getSortIconLevel3(filial.codigo, 'descricao') }}
                                      </v-icon>
                                    </div>
                                  </th>
                                  <th style="width: 110px;" class="sortable-header" @click="sortTableLevel3(filial.codigo, 'tipo')">
                                    <div class="header-content">
                                      <span>Tipo</span>
                                      <v-icon x-small :color="isSortActiveLevel3(filial.codigo, 'tipo') ? '#023449' : 'rgba(2, 52, 73, 0.5)'">
                                        {{ getSortIconLevel3(filial.codigo, 'tipo') }}
                                      </v-icon>
                                    </div>
                                  </th>
                                  <th style="width: 110px;" class="sortable-header" @click="sortTableLevel3(filial.codigo, 'status')">
                                    <div class="header-content">
                                      <span>Status</span>
                                      <v-icon x-small :color="isSortActiveLevel3(filial.codigo, 'status') ? '#023449' : 'rgba(2, 52, 73, 0.5)'">
                                        {{ getSortIconLevel3(filial.codigo, 'status') }}
                                      </v-icon>
                                    </div>
                                  </th>
                                  <th class="text-center sortable-header" @click="sortTableLevel3(filial.codigo, 'quantidadeEstoque')">
                                    <div class="header-content">
                                      <span>Qtd Estoque</span>
                                      <v-icon x-small :color="isSortActiveLevel3(filial.codigo, 'quantidadeEstoque') ? '#023449' : 'rgba(2, 52, 73, 0.5)'">
                                        {{ getSortIconLevel3(filial.codigo, 'quantidadeEstoque') }}
                                      </v-icon>
                                    </div>
                                  </th>
                                  <th class="text-center sortable-header" @click="sortTableLevel3(filial.codigo, 'quantidadeContada')">
                                    <div class="header-content">
                                      <span>Qtd Contada</span>
                                      <v-icon x-small :color="isSortActiveLevel3(filial.codigo, 'quantidadeContada') ? '#023449' : 'rgba(2, 52, 73, 0.5)'">
                                        {{ getSortIconLevel3(filial.codigo, 'quantidadeContada') }}
                                      </v-icon>
                                    </div>
                                  </th>
                                  <th class="text-center sortable-header" @click="sortTableLevel3(filial.codigo, 'diferencaQuantidade')">
                                    <div class="header-content">
                                      <span>Diff Qtd</span>
                                      <v-icon x-small :color="isSortActiveLevel3(filial.codigo, 'diferencaQuantidade') ? '#023449' : 'rgba(2, 52, 73, 0.5)'">
                                        {{ getSortIconLevel3(filial.codigo, 'diferencaQuantidade') }}
                                      </v-icon>
                                    </div>
                                  </th>
                                  <th class="text-right sortable-header" @click="sortTableLevel3(filial.codigo, 'valorEstoque')">
                                    <div class="header-content">
                                      <span>Valor Estoque</span>
                                      <v-icon x-small :color="isSortActiveLevel3(filial.codigo, 'valorEstoque') ? '#023449' : 'rgba(2, 52, 73, 0.5)'">
                                        {{ getSortIconLevel3(filial.codigo, 'valorEstoque') }}
                                      </v-icon>
                                    </div>
                                  </th>
                                  <th class="text-right sortable-header" @click="sortTableLevel3(filial.codigo, 'valorContado')">
                                    <div class="header-content">
                                      <span>Valor Contado</span>
                                      <v-icon x-small :color="isSortActiveLevel3(filial.codigo, 'valorContado') ? '#023449' : 'rgba(2, 52, 73, 0.5)'">
                                        {{ getSortIconLevel3(filial.codigo, 'valorContado') }}
                                      </v-icon>
                                    </div>
                                  </th>
                                  <th class="text-right sortable-header" @click="sortTableLevel3(filial.codigo, 'diferencaValor')">
                                    <div class="header-content">
                                      <span>Diff Valor</span>
                                      <v-icon x-small :color="isSortActiveLevel3(filial.codigo, 'diferencaValor') ? '#023449' : 'rgba(2, 52, 73, 0.5)'">
                                        {{ getSortIconLevel3(filial.codigo, 'diferencaValor') }}
                                      </v-icon>
                                    </div>
                                  </th>
                                </tr>
                                </thead>
                                <tbody>
                                <!-- Renderização paginada de produtos -->
                                <tr
                                    v-for="produto in getPaginatedProdutos(filial.codigo, dia.data, dia.produtos)"
                                    :key="`produto-${produto.codigo}`"
                                    class="level-3-row"
                                >
                                  <td></td>
                                  <td>
                                    <span class="produto-codigo">{{ produto.codigo }}</span>
                                  </td>
                                  <td>
                                    <div class="produto-descricao">
                                      <v-tooltip bottom color="#023449" content-class="tooltip-no-wrap">
                                        <template v-slot:activator="{ on, attrs }">
                                          <span
                                              class="text-truncate font-weight-medium text-caption"
                                              v-bind="attrs"
                                              v-on="on"
                                          >
                                            {{ produto.descricao }}
                                          </span>
                                        </template>
                                        <span class="white--text">{{ produto.descricao }}</span>
                                      </v-tooltip>
                                    </div>
                                  </td>
                                  <td>
                                    <span class="tipo-badge">{{ produto.tipo }}</span>
                                  </td>
                                  <td>
                                    <span :class="['status-badge', getStatusClass(produto.status)]">
                                      {{ produto.status }}
                                    </span>
                                  </td>
                                  <td class="text-center">
                                    <span class="qty-value">{{ produto.quantidadeEstoque }}</span>
                                  </td>
                                  <td class="text-center">
                                    <span class="qty-value">{{ produto.quantidadeContada }}</span>
                                  </td>
                                  <td class="text-center">
                                    <span :class="['diff-value', getDiffClass(produto.diferencaQuantidade)]">
                                      {{ formatDiff(produto.diferencaQuantidade) }}
                                    </span>
                                  </td>
                                  <td class="text-right">{{ formatCurrency(produto.valorEstoque) }}</td>
                                  <td class="text-right">{{ formatCurrency(produto.valorContado) }}</td>
                                  <td class="text-right text-truncate">
                                    <span :class="['diff-value', getDiffClass(produto.diferencaValor)]">
                                      {{ formatCurrency(produto.diferencaValor) }}
                                    </span>
                                  </td>
                                </tr>

                                <!-- Paginação de Produtos -->
                                <tr v-if="dia.produtos && dia.produtos.length > produtosPerPage" class="produtos-pagination-row">
                                  <td colspan="11" class="produtos-pagination-cell">
                                    <div class="produtos-pagination">
                                      <div class="produtos-pagination-info">
                                        <v-icon small color="#64748b" class="mr-2">mdi-package-variant</v-icon>
                                        Mostrando
                                        <strong>{{ getProdutosStartIndex(filial.codigo, dia.data) + 1 }}</strong> -
                                        <strong>{{ getProdutosEndIndex(filial.codigo, dia.data, dia.produtos.length) }}</strong>
                                        de <strong>{{ dia.produtos.length }}</strong> produtos
                                      </div>
                                      <div class="produtos-pagination-controls">
                                        <v-btn
                                            icon
                                            x-small
                                            :disabled="getProdutosCurrentPage(filial.codigo, dia.data) === 1"
                                            @click="changeProdutosPage(filial.codigo, dia.data, 1)"
                                            color="#023449"
                                        >
                                          <v-icon x-small>mdi-page-first</v-icon>
                                        </v-btn>
                                        <v-btn
                                            text
                                            x-small
                                            :disabled="getProdutosCurrentPage(filial.codigo, dia.data) === 1"
                                            @click="previousProdutosPage(filial.codigo, dia.data)"
                                            color="#023449"
                                        >
                                          <v-icon left x-small>mdi-chevron-left</v-icon>
                                          Anterior
                                        </v-btn>
                                        <div class="produtos-page-info">
                                          Página {{ getProdutosCurrentPage(filial.codigo, dia.data) }} de {{ getProdutosTotalPages(dia.produtos.length) }}
                                        </div>
                                        <v-btn
                                            text
                                            x-small
                                            :disabled="getProdutosCurrentPage(filial.codigo, dia.data) >= getProdutosTotalPages(dia.produtos.length)"
                                            @click="nextProdutosPage(filial.codigo, dia.data)"
                                            color="#023449"
                                        >
                                          Próxima
                                          <v-icon right x-small>mdi-chevron-right</v-icon>
                                        </v-btn>
                                        <v-btn
                                            icon
                                            x-small
                                            :disabled="getProdutosCurrentPage(filial.codigo, dia.data) >= getProdutosTotalPages(dia.produtos.length)"
                                            @click="changeProdutosPage(filial.codigo, dia.data, getProdutosTotalPages(dia.produtos.length))"
                                            color="#023449"
                                        >
                                          <v-icon x-small>mdi-page-last</v-icon>
                                        </v-btn>
                                      </div>
                                    </div>
                                  </td>
                                </tr>
                                </tbody>
                              </table>
                            </td>
                          </tr>
                        </template>
                      </template>
                      </tbody>
                    </table>
                  </td>
                </tr>
              </template>
            </template>
            </tbody>

            <!-- Linha de Totais -->
            <tfoot v-if="filteredData.length > 0" class="table-footer">
            <tr class="totals-row">
              <td></td>
              <td>
                <div class="cell-content">
                  <v-icon small color="#023449" class="mr-2">mdi-sigma</v-icon>
                  <strong>TOTAL</strong>
                </div>
              </td>
              <td class="text-center">
                <v-chip small color="#e0f2fe" text-color="#0c4a6e" class="font-weight-bold">
                  {{ data_totais.quantidadeLancamentosRealizados }} / {{ data_totais.quantidadeLancamentos }}
                </v-chip>
              </td>
              <td class="text-center">
                <v-chip small color="#dbeafe" text-color="#1e40af" class="font-weight-bold">
                  {{ data_totais.quantidadeSkus }}
                </v-chip>
              </td>
              <td class="text-center">
                <strong>{{ formatCurrency(data_totais.valorEstoqueAnterior) }}</strong>
              </td>
              <td class="text-center">
                <strong>{{ formatCurrency(data_totais.valorEstoqueContado) }}</strong>
              </td>
              <td class="text-center">
                <span :class="['valor-divergencia', 'total-value', getDivergenciaClass(data_totais.valorDivergencia)]">
                  {{ formatCurrency(data_totais.valorDivergencia) }}
                </span>
              </td>
              <td class="text-center py-2">
                <v-progress-circular
                    :value="Math.min(data_totais.percentualDivergencia || 0, 100)"
                    :color="getPercentualColor(data_totais.percentualDivergencia || 0)"
                    :size="60"
                    :width="2"
                >
                  <span class="percentual-text">
                    {{
                      Math.abs(data_totais.percentualDivergencia || 0) >= 100
                          ? (data_totais.percentualDivergencia || 0).toFixed(0)
                          : (data_totais.percentualDivergencia || 0).toFixed(2)
                    }}%
                  </span>
                </v-progress-circular>
              </td>
              <td class="text-center">
                <v-chip
                    small
                    :color="getStatusColor(calculateStatus(data_totais.percentualDivergencia))"
                    :text-color="getStatusTextColor(calculateStatus(data_totais.percentualDivergencia))"
                    class="status-chip"
                >
                  <v-icon left x-small>{{ getStatusIcon(calculateStatus(data_totais.percentualDivergencia)) }}</v-icon>
                  {{ calculateStatus(data_totais.percentualDivergencia) }}
                </v-chip>
              </td>
            </tr>
            </tfoot>
          </table>
        </div>
      </v-card-text>
    </v-card>

    <!-- Pagination -->
    <div class="pagination" v-if="!loading && !isFullscreen && filteredData.length > 0">
      <div class="pagination-left">
        <div class="pagination-info">
          <v-icon small color="#64748b" class="mr-2">mdi-information</v-icon>
          <template v-if="itemsPerPage === -1">
            Mostrando <strong>{{ filteredData.length }}</strong> filia{{ filteredData.length !== 1 ? 'is' : 'l' }}
          </template>
          <template v-else>
            Mostrando
            <strong>{{ startIndex + 1 }}</strong> - <strong>{{ endIndex }}</strong>
            de <strong>{{ filteredData.length }}</strong> filiais
          </template>
        </div>
        <div class="items-per-page">
          <span class="items-label">Itens por página:</span>
          <v-select
              :items="itemsPerPageOptions"
              v-model="itemsPerPage"
              @change="changeItemsPerPage"
              dense
              outlined
              hide-details
              item-text="text"
              item-value="value"
              class="items-select"
          ></v-select>
        </div>
      </div>
      <div class="pagination-controls" v-if="itemsPerPage !== -1">
        <v-btn icon small :disabled="currentPage === 1" @click="goToPage(1)" color="#023449">
          <v-icon small>mdi-page-first</v-icon>
        </v-btn>
        <v-btn text small :disabled="currentPage === 1" @click="previousPage" color="#023449">
          <v-icon left small>mdi-chevron-left</v-icon>
          Anterior
        </v-btn>
        <div class="page-numbers">
          <v-btn
              v-for="page in visiblePages"
              :key="page"
              small
              :outlined="page !== currentPage"
              :color="page === currentPage ? '#023449' : ''"
              :dark="page === currentPage"
              @click="goToPage(page)"
              class="page-btn"
          >
            {{ page }}
          </v-btn>
        </div>
        <v-btn text small :disabled="currentPage === totalPages" @click="nextPage" color="#023449">
          Próxima
          <v-icon right small>mdi-chevron-right</v-icon>
        </v-btn>
        <v-btn icon small :disabled="currentPage === totalPages" @click="goToPage(totalPages)" color="#023449">
          <v-icon small>mdi-page-last</v-icon>
        </v-btn>
      </div>
    </div>
  </div>
</template>

<script>
import FiliaisViewFilters from './FiliaisViewFilters.vue';
import {mapState} from "vuex";

export default {
  name: 'FiliaisView',
  components: {
    DataTableFilters: FiliaisViewFilters
  },
  props: {
    data: {
      type: Array,
      default: () => []
    },
    data_totais: {
      type: Object,
      default: () => ({})
    },
    filters: {
      type: Object,
      default: () => ({})
    },
    loading: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      currentPage: 1,
      itemsPerPage: -1,
      itemsPerPageOptions: [
        { text: '5', value: 5 },
        { text: '10', value: 10 },
        { text: '20', value: 20 },
        { text: 'Todos', value: -1 }
      ],
      expandedFiliais: [],
      expandedDias: {},
      sortBy: null,
      sortOrder: 'asc',
      isFullscreen: false,
      sortByFilial: {},
      // Paginação de produtos
      produtosPerPage: 50,
      produtosPagination: {}
    };
  },

  computed: {
    ...mapState(["backendUrl", "usuario"]),

    filteredData() {
      let data = [...this.data];

      data = data.map(filial => {
        let sortedFilial = {
          ...filial,
          status: this.calculateStatus(filial.percentualDivergencia)
        };

        if (filial.dias && this.sortByFilial[filial.codigo]) {
          const sortConfig = this.sortByFilial[filial.codigo];
          if (sortConfig.level === 2) {
            sortedFilial.dias = this.sortDias([...filial.dias], sortConfig.field, sortConfig.order);
          } else if (sortConfig.level === 3) {
            sortedFilial.dias = filial.dias.map(dia => ({
              ...dia,
              produtos: dia.produtos ? this.sortProdutos([...dia.produtos], sortConfig.field, sortConfig.order) : []
            }));
          }
        }

        return sortedFilial;
      });

      if (this.sortBy) {
        data.sort((a, b) => {
          let valueA = this.getSortValue(a, this.sortBy);
          let valueB = this.getSortValue(b, this.sortBy);

          if (valueA === null || valueA === undefined) return 1;
          if (valueB === null || valueB === undefined) return -1;

          if (valueA < valueB) return this.sortOrder === 'asc' ? -1 : 1;
          if (valueA > valueB) return this.sortOrder === 'asc' ? 1 : -1;
          return 0;
        });
      }

      return data;
    },

    totalLancamentos() {
      return this.filteredData.reduce((sum, filial) => sum + filial.quantidadeLancamentos, 0);
    },

    totalPages() {
      if (this.itemsPerPage === -1) return 1;
      return Math.ceil(this.filteredData.length / this.itemsPerPage);
    },

    startIndex() {
      if (this.itemsPerPage === -1) return 0;
      return (this.currentPage - 1) * this.itemsPerPage;
    },

    endIndex() {
      if (this.itemsPerPage === -1) return this.filteredData.length;
      const end = this.startIndex + this.itemsPerPage;
      return end > this.filteredData.length ? this.filteredData.length : end;
    },

    paginatedData() {
      if (this.isFullscreen) return this.filteredData;
      if (this.itemsPerPage === -1) return this.filteredData;
      return this.filteredData.slice(this.startIndex, this.endIndex);
    },

    totals() {
      let valorEstoqueAnterior = this.filteredData.reduce((sum, f) => sum + (f.valorEstoqueAnterior || 0), 0);
      let valorEstoqueContado = this.filteredData.reduce((sum, f) => sum + (f.valorEstoqueContado || 0), 0);
      // let valorEstoqueDivergencia = this.filteredData.reduce((sum, f) => sum + (f.valorEstoqueContado || 0), 0);

      return {
        quantidadeLancamentos: this.filteredData.reduce((sum, f) => sum + (f.quantidadeLancamentos || 0), 0),
        quantidadeLancamentosRealizados: this.filteredData.reduce((sum, f) => sum + (f.quantidadeLancamentosRealizados || 0), 0),
        quantidadeSkus: this.filteredData.reduce((sum, f) => sum + (f.quantidadeSkus || 0), 0),
        valorEstoqueAnterior: valorEstoqueAnterior,
        valorEstoqueContado: valorEstoqueContado,
        valorDivergencia: valorEstoqueContado - valorEstoqueAnterior,
        percentualDivergencia: valorEstoqueAnterior > 0
            ? (valorEstoqueContado / valorEstoqueAnterior - 1) * 100
            : -100
      };
    },

    visiblePages() {
      const pages = [];
      const maxVisible = 5;
      let start = Math.max(1, this.currentPage - Math.floor(maxVisible / 2));
      let end = Math.min(this.totalPages, start + maxVisible - 1);

      if (end - start < maxVisible - 1) {
        start = Math.max(1, end - maxVisible + 1);
      }

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      return pages;
    }
  },

  methods: {
    getStatusClass(status) {
      if (status === 'FINALIZADO') {
        return 'status-concluido';
      } else if (status === 'PENDENTE') {
        return 'status-aberto';
      }
      return '';
    },

    toggleFilial(filialId) {
      const index = this.expandedFiliais.indexOf(filialId);
      if (index > -1) {
        this.expandedFiliais.splice(index, 1);
      } else {
        this.expandedFiliais.push(filialId);
      }
    },

    toggleDia(filialId, data) {
      const key = `${filialId}-${data}`;
      if (!this.expandedDias[key]) {
        this.$set(this.expandedDias, key, true);
        this.$set(this.produtosPagination, key, 1);
      } else {
        this.$set(this.expandedDias, key, false);
      }
    },

    isDiaExpanded(filialId, data) {
      return this.expandedDias[`${filialId}-${data}`] || false;
    },

    expandAll() {
      this.expandedFiliais = this.paginatedData.map(f => f.codigo);
      this.paginatedData.forEach(filial => {
        filial.dias.forEach(dia => {
          const key = `${filial.codigo}-${dia.data}`;
          this.$set(this.expandedDias, key, true);
          this.$set(this.produtosPagination, key, 1);
        });
      });
    },

    collapseAll() {
      this.expandedFiliais = [];
      this.expandedDias = {};
      this.produtosPagination = {};
    },

    getProdutosKey(filialId, data) {
      return `${filialId}-${data}`;
    },

    getProdutosCurrentPage(filialId, data) {
      const key = this.getProdutosKey(filialId, data);
      return this.produtosPagination[key] || 1;
    },

    getProdutosTotalPages(totalProdutos) {
      return Math.ceil(totalProdutos / this.produtosPerPage);
    },

    getProdutosStartIndex(filialId, data) {
      const currentPage = this.getProdutosCurrentPage(filialId, data);
      return (currentPage - 1) * this.produtosPerPage;
    },

    getProdutosEndIndex(filialId, data, totalProdutos) {
      const startIndex = this.getProdutosStartIndex(filialId, data);
      const endIndex = startIndex + this.produtosPerPage;
      return endIndex > totalProdutos ? totalProdutos : endIndex;
    },

    getPaginatedProdutos(filialId, data, produtos) {
      if (!produtos || produtos.length <= this.produtosPerPage) {
        return produtos || [];
      }

      const startIndex = this.getProdutosStartIndex(filialId, data);
      const endIndex = this.getProdutosEndIndex(filialId, data, produtos.length);

      return produtos.slice(startIndex, endIndex);
    },

    changeProdutosPage(filialId, data, page) {
      const key = this.getProdutosKey(filialId, data);
      this.$set(this.produtosPagination, key, page);
    },

    previousProdutosPage(filialId, data) {
      const currentPage = this.getProdutosCurrentPage(filialId, data);
      if (currentPage > 1) {
        this.changeProdutosPage(filialId, data, currentPage - 1);
      }
    },

    nextProdutosPage(filialId, data) {
      const dia = this.findDia(filialId, data);
      if (dia && dia.produtos) {
        const totalPages = this.getProdutosTotalPages(dia.produtos.length);
        const currentPage = this.getProdutosCurrentPage(filialId, data);
        if (currentPage < totalPages) {
          this.changeProdutosPage(filialId, data, currentPage + 1);
        }
      }
    },

    findDia(filialId, data) {
      const filial = this.filteredData.find(f => f.codigo === filialId);
      if (filial && filial.dias) {
        return filial.dias.find(d => d.data === data);
      }
      return null;
    },

    formatDate(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString + "T00:00:00");
      return date.toLocaleDateString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric"
      });
    },

    formatCurrency(value) {
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL'
      }).format(value || 0);
    },

    formatDiff(value) {
      const val = value || 0;
      const formatted = val >= 0 ? `+${val}` : val;
      return formatted;
    },

    getDivergenciaClass(value) {
      const val = value || 0;
      if (val === 0) return 'neutral';
      return val < 0 ? 'negative' : 'positive';
    },

    getDiffClass(value) {
      const val = value || 0;
      if (val === 0) return 'neutral';
      return val < 0 ? 'negative' : 'positive';
    },

    getPercentualColor(value) {
      const val = value || 0;
      if (val === 0) return 'grey darken-1';
      if (val > 0) return '#2e7d32';
      return '#c62828';
    },

    getPercentualBadgeClass(value) {
      const val = value || 0;
      if (val === 0) return 'grey lighten-2';
      if (val > 0) return 'good';
      return 'danger';
    },

    getStatusColor(status) {
      const colors = {
        'Conforme': '#d1fae5',
        'Atenção': '#fed7aa',
        'Crítico': '#fecaca'
      };
      return colors[status] || '#f3f4f6';
    },

    getStatusTextColor(status) {
      const colors = {
        'Conforme': '#047857',
        'Atenção': '#c2410c',
        'Crítico': '#991b1b'
      };
      return colors[status] || '#374151';
    },

    getStatusIcon(status) {
      const icons = {
        'Conforme': 'mdi-check-circle',
        'Atenção': 'mdi-alert',
        'Crítico': 'mdi-alert-octagon'
      };
      return icons[status] || 'mdi-information';
    },

    previousPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
      }
    },

    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
      }
    },

    goToPage(page) {
      this.currentPage = page;
    },

    sortTable(field) {
      if (this.sortBy === field) {
        this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortBy = field;
        this.sortOrder = 'asc';
      }
      this.currentPage = 1;
    },

    getSortValue(item, field) {
      const fieldMap = {
        'filial': item.empresa,
        'lancamentos': item.quantidadeLancamentosRealizados,
        'skus': item.quantidadeSkus,
        'valorEstoque': item.valorEstoqueAnterior,
        'valorContado': item.valorEstoqueContado,
        'valorDivergencia': item.valorDivergencia,
        'percentualDivergencia': item.percentualDivergencia,
        'status': item.status
      };
      return fieldMap[field];
    },

    getSortIcon(field) {
      if (this.sortBy !== field) return 'mdi-sort';
      return this.sortOrder === 'asc' ? 'mdi-sort-ascending' : 'mdi-sort-descending';
    },

    isSortActive(field) {
      return this.sortBy === field;
    },

    calculateStatus(percentualDivergencia) {
      const percentual = percentualDivergencia || 0;

      if (percentual > -0.51 && percentual < 0.51) {
        return 'Conforme';
      } else if (percentual > -0.71 && percentual < 0.71) {
        return 'Atenção';
      } else {
        return 'Crítico';
      }
    },

    changeItemsPerPage(value) {
      this.itemsPerPage = value;
      this.currentPage = 1;
    },

    toggleFullscreen() {
      this.isFullscreen = !this.isFullscreen;

      if (this.isFullscreen) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = '';
      }
    },

    sortTableLevel2(filialCodigo, field) {
      if (!this.sortByFilial[filialCodigo] || this.sortByFilial[filialCodigo].level !== 2 || this.sortByFilial[filialCodigo].field !== field) {
        this.$set(this.sortByFilial, filialCodigo, { level: 2, field, order: 'asc' });
      } else {
        const currentOrder = this.sortByFilial[filialCodigo].order;
        this.$set(this.sortByFilial, filialCodigo, { level: 2, field, order: currentOrder === 'asc' ? 'desc' : 'asc' });
      }
    },

    sortTableLevel3(filialCodigo, field) {
      if (!this.sortByFilial[filialCodigo] || this.sortByFilial[filialCodigo].level !== 3 || this.sortByFilial[filialCodigo].field !== field) {
        this.$set(this.sortByFilial, filialCodigo, { level: 3, field, order: 'asc' });
      } else {
        const currentOrder = this.sortByFilial[filialCodigo].order;
        this.$set(this.sortByFilial, filialCodigo, { level: 3, field, order: currentOrder === 'asc' ? 'desc' : 'asc' });
      }
    },

    sortDias(dias, field, order) {
      return dias.sort((a, b) => {
        let valueA = this.getDiaSortValue(a, field);
        let valueB = this.getDiaSortValue(b, field);

        if (valueA === null || valueA === undefined) return 1;
        if (valueB === null || valueB === undefined) return -1;

        if (valueA < valueB) return order === 'asc' ? -1 : 1;
        if (valueA > valueB) return order === 'asc' ? 1 : -1;
        return 0;
      });
    },

    sortProdutos(produtos, field, order) {
      return produtos.sort((a, b) => {
        let valueA = this.getProdutoSortValue(a, field);
        let valueB = this.getProdutoSortValue(b, field);

        if (valueA === null || valueA === undefined) return 1;
        if (valueB === null || valueB === undefined) return -1;

        if (valueA < valueB) return order === 'asc' ? -1 : 1;
        if (valueA > valueB) return order === 'asc' ? 1 : -1;
        return 0;
      });
    },

    getDiaSortValue(dia, field) {
      const fieldMap = {
        'data': dia.data,
        'skus': dia.quantidadeSkus,
        'valorEstoque': dia.valorEstoque,
        'valorContado': dia.valorContado,
        'valorDivergencia': dia.valorDivergencia,
        'percentualDivergencia': dia.percentualDivergencia
      };
      return fieldMap[field];
    },

    getProdutoSortValue(produto, field) {
      const fieldMap = {
        'codigo': produto.codigo,
        'descricao': produto.descricao,
        'tipo': produto.tipo,
        'status': produto.status,
        'quantidadeEstoque': produto.quantidadeEstoque,
        'quantidadeContada': produto.quantidadeContada,
        'diferencaQuantidade': produto.diferencaQuantidade,
        'valorEstoque': produto.valorEstoque,
        'valorContado': produto.valorContado,
        'diferencaValor': produto.diferencaValor
      };
      return fieldMap[field];
    },

    getSortIconLevel2(filialCodigo, field) {
      if (!this.sortByFilial[filialCodigo] || this.sortByFilial[filialCodigo].level !== 2 || this.sortByFilial[filialCodigo].field !== field) {
        return 'mdi-sort';
      }
      return this.sortByFilial[filialCodigo].order === 'asc' ? 'mdi-sort-ascending' : 'mdi-sort-descending';
    },

    getSortIconLevel3(filialCodigo, field) {
      if (!this.sortByFilial[filialCodigo] || this.sortByFilial[filialCodigo].level !== 3 || this.sortByFilial[filialCodigo].field !== field) {
        return 'mdi-sort';
      }
      return this.sortByFilial[filialCodigo].order === 'asc' ? 'mdi-sort-ascending' : 'mdi-sort-descending';
    },

    isSortActiveLevel2(filialCodigo, field) {
      return this.sortByFilial[filialCodigo] && this.sortByFilial[filialCodigo].level === 2 && this.sortByFilial[filialCodigo].field === field;
    },

    isSortActiveLevel3(filialCodigo, field) {
      return this.sortByFilial[filialCodigo] && this.sortByFilial[filialCodigo].level === 3 && this.sortByFilial[filialCodigo].field === field;
    }
  },

  beforeDestroy() {
    if (this.isFullscreen) {
      document.body.style.overflow = '';
    }
  }
};
</script>

<style scoped>
.status-badge {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
}

.status-concluido {
  background-color: #e8f5e9;
  color: #2e7d32;
}

.status-aberto {
  background-color: #fff3e0;
  color: #e65100;
}

.data-table-container {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 20px;
  transition: all 0.3s ease;
}

.data-table-container.fullscreen-mode {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100vw;
  height: 100vh;
  background: #f8fafc;
  z-index: 9999;
  padding: 20px;
  overflow: hidden;
  gap: 20px;
  display: flex;
  flex-direction: column;
}

.fullscreen-mode .table-header {
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(2, 52, 73, 0.08);
}

.fullscreen-mode .table-card {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: 0;
}

.fullscreen-mode .table-card .v-card__text {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: 0;
  position: relative;
}

.fullscreen-mode .table-wrapper {
  flex: 1;
  overflow: auto;
  min-height: 0;
  position: relative;
}

.fullscreen-mode .level-1-header {
  position: sticky;
  top: 0;
  z-index: 100;
  box-shadow: 0 2px 4px rgba(2, 52, 73, 0.1);
}

.fullscreen-mode .pagination {
  flex-shrink: 0;
  box-shadow: 0 -2px 8px rgba(2, 52, 73, 0.08);
}

.table-card {
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  overflow: hidden;
}

.table-wrapper {
  overflow-x: auto;
  max-width: 100%;
  position: relative;
}

.table-loading,
.table-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  gap: 16px;
  padding: 40px;
}

.table-loading p {
  margin: 0;
  color: #64748b;
  font-size: 0.875rem;
  font-weight: 500;
}

.table-empty h3 {
  margin: 0;
  color: #023449;
  font-size: 1.25rem;
  font-weight: 700;
}

.table-empty p {
  color: #64748b;
  font-size: 0.875rem;
  margin: 0;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  background: #ffffff;
}

.nested-table {
  width: 100%;
  border-collapse: collapse;
}

.level-1-header {
  background: #023449;
}

.level-1-header th {
  padding: 16px;
  text-align: left;
  font-weight: 700;
  font-size: 0.75rem;
  color: #ffffff;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border: none;
}

.table-footer {
  background: #f1f5f9;
  border-top: 3px solid #023449;
}

.totals-row {
  font-weight: 700;
  background: #f8fafc;
}

.totals-row td {
  padding: 16px;
  font-size: 0.875rem;
  color: #023449;
  vertical-align: middle;
  border-top: 2px solid #cbd5e1;
}

.totals-row .cell-content {
  font-size: 0.938rem;
  font-weight: 800;
  color: #023449;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.totals-row .total-value {
  font-size: 1rem;
  font-weight: 800;
}

.sortable-header {
  cursor: pointer;
  user-select: none;
  transition: background-color 0.2s ease;
}

.sortable-header:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

.sortable-header .header-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.sortable-header.text-center .header-content {
  justify-content: center;
}

.sortable-header .header-content span {
  flex: 1;
}

.sortable-header .header-content .v-icon {
  flex-shrink: 0;
  transition: all 0.2s ease;
}

.sortable-header:hover .header-content .v-icon {
  opacity: 1 !important;
  color: #ffffff !important;
}

.level-1-row {
  cursor: pointer;
  transition: background-color 0.2s ease;
  border-bottom: 2px solid #e2e8f0;
}

.level-1-row:hover {
  background-color: #f8fafc;
}

.level-1-row td {
  padding: 16px;
  font-size: 0.875rem;
  color: #374151;
  vertical-align: middle;
}

.level-2-header-row {
  background: #f8fafc;
}

.level-2-table {
  margin: 0;
  background: #f8fafc;
}

.level-2-table thead {
  background: #cbd5e1;
}

.level-2-table thead th {
  padding: 12px 16px;
  text-align: left;
  font-weight: 600;
  font-size: 0.75rem;
  color: #023449;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.level-2-table .sortable-header {
  cursor: pointer;
  user-select: none;
  transition: background-color 0.2s ease;
}

.level-2-table .sortable-header:hover {
  background-color: rgba(2, 52, 73, 0.08);
}

.level-2-table .sortable-header .header-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
}

.level-2-table .sortable-header.text-center .header-content,
.level-2-table .sortable-header.text-right .header-content {
  justify-content: center;
}

.level-2-table .sortable-header .header-content .v-icon {
  flex-shrink: 0;
  transition: all 0.2s ease;
}

.level-2-table .sortable-header:hover .header-content .v-icon {
  opacity: 1 !important;
  color: #023449 !important;
}

.level-2-row {
  cursor: pointer;
  transition: background-color 0.2s ease;
  border-bottom: 1px solid #e2e8f0;
}

.level-2-row:hover {
  background-color: #ffffff;
}

.level-2-row td {
  padding: 12px 16px;
  font-size: 0.813rem;
  color: #475569;
}

.level-3-table {
  margin: 0;
  background: #ffffff;
}

.level-3-table thead {
  background: #e0e7ff;
}

.level-3-table thead th {
  padding: 10px 12px;
  text-align: left;
  font-weight: 600;
  font-size: 0.688rem;
  color: #023449;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.level-3-table .sortable-header {
  cursor: pointer;
  user-select: none;
  transition: background-color 0.2s ease;
}

.level-3-table .sortable-header:hover {
  background-color: rgba(2, 52, 73, 0.08);
}

.level-3-table .sortable-header .header-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
}

.level-3-table .sortable-header.text-center .header-content,
.level-3-table .sortable-header.text-right .header-content {
  justify-content: center;
}

.level-3-table .sortable-header .header-content .v-icon {
  flex-shrink: 0;
  transition: all 0.2s ease;
}

.level-3-table .sortable-header:hover .header-content .v-icon {
  opacity: 1 !important;
  color: #023449 !important;
}

.level-3-row {
  border-bottom: 1px solid #f1f5f9;
  transition: background-color 0.2s ease;
}

.level-3-row:hover {
  background-color: #fafafa;
}

.level-3-row td {
  padding: 10px 12px;
  font-size: 0.75rem;
  color: #64748b;
}

.cell-content {
  display: flex;
  align-items: center;
}

.filial-code {
  color: #94a3b8;
  font-size: 0.813rem;
}

.produto-codigo {
  font-family: 'Courier New', monospace;
  font-weight: 700;
  color: #023449;
  background: #f1f5f9;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.75rem;
}

.produto-descricao {
  color: #334155;
  font-weight: 500;
  display: flex;
  align-items: center;
  min-width: 0;
  max-width: 250px;
  overflow: hidden;
}

.quantity-badge {
  display: inline-block;
  padding: 4px 10px;
  background: #dbeafe;
  border-radius: 6px;
  color: #1e40af;
  font-weight: 700;
  font-size: 0.75rem;
}

.qty-value {
  font-weight: 600;
  color: #334155;
  font-size: 0.813rem;
}

.valor-divergencia {
  font-weight: 700;
  font-size: 0.875rem;
}

.valor-divergencia.positive {
  color: #047857;
}

.valor-divergencia.negative {
  color: #c62828;
}

.valor-divergencia.neutral {
  color: #64748b;
}

.diff-value {
  font-weight: 700;
  font-size: 0.813rem;
  padding: 4px 8px;
  border-radius: 4px;
  display: inline-block;
}

.diff-value.positive {
  background: #d1fae5;
  color: #047857;
}

.diff-value.negative {
  background: #fecaca;
  color: #991b1b;
}

.diff-value.neutral {
  background: #f3f4f6;
  color: #64748b;
}

.percentual-text {
  font-size: 0.863rem;
  font-weight: 700;
  line-height: 1.2;
  white-space: nowrap;
}

.percentual-badge {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 6px;
  font-weight: 700;
  font-size: 0.75rem;
  border: 1px solid;
}

.percentual-badge.good {
  background: #d1fae5;
  border-color: #6ee7b7;
  color: #047857;
}

.percentual-badge.warning {
  background: #fed7aa;
  border-color: #fdba74;
  color: #c2410c;
}

.percentual-badge.danger {
  background: #fecaca;
  border-color: #fca5a5;
  color: #991b1b;
}

.status-chip {
  font-weight: 700 !important;
  letter-spacing: 0.3px;
  border: 1px solid currentColor !important;
}

.tipo-badge {
  display: inline-block;
  padding: 4px 8px;
  background: #e0e7ff;
  border-radius: 4px;
  color: #3730a3;
  font-size: 0.688rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.text-center {
  text-align: center;
}

.text-right {
  text-align: right;
}

.pagination {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  flex-wrap: wrap;
  gap: 16px;
  box-shadow: 0 1px 3px rgba(2, 52, 73, 0.04);
}

.pagination-left {
  display: flex;
  align-items: center;
  gap: 24px;
  flex-wrap: wrap;
}

.pagination-info {
  display: flex;
  align-items: center;
  color: #64748b;
  font-size: 0.875rem;
  font-weight: 500;
}

.pagination-info strong {
  color: #023449;
  font-weight: 700;
  margin: 0 4px;
}

.items-per-page {
  display: flex;
  align-items: center;
  gap: 12px;
}

.items-label {
  color: #64748b;
  font-size: 0.875rem;
  font-weight: 500;
  white-space: nowrap;
}

.items-select {
  max-width: 90px;
  font-weight: 600;
}

.items-select >>> .v-input__control {
  min-height: 36px !important;
}

.items-select >>> .v-select__selections {
  padding: 0 8px;
  font-size: 0.875rem;
  font-weight: 600;
  color: #023449;
}

.items-select >>> .v-input__slot {
  min-height: 36px !important;
  padding: 0 8px !important;
}

.items-select >>> fieldset {
  border-color: #cbd5e1;
}

.items-select >>> .v-select__selection {
  margin: 0 !important;
}

.pagination-controls {
  display: flex;
  gap: 8px;
  align-items: center;
}

.page-numbers {
  display: flex;
  gap: 4px;
}

.page-btn {
  text-transform: none !important;
  font-weight: 700 !important;
  min-width: 40px !important;
}

.produtos-pagination-row {
  background: #f8fafc;
  border-top: 2px solid #cbd5e1;
}

.produtos-pagination-cell {
  padding: 12px 16px !important;
}

.produtos-pagination {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.produtos-pagination-info {
  display: flex;
  align-items: center;
  color: #64748b;
  font-size: 0.813rem;
  font-weight: 500;
}

.produtos-pagination-info strong {
  color: #023449;
  font-weight: 700;
  margin: 0 4px;
}

.produtos-pagination-controls {
  display: flex;
  gap: 6px;
  align-items: center;
}

.produtos-page-info {
  padding: 0 12px;
  font-size: 0.813rem;
  font-weight: 600;
  color: #023449;
  white-space: nowrap;
}

@media (max-width: 1200px) {
  .table-controls {
    flex-wrap: wrap;
  }
}

@media (max-width: 768px) {
  .table-wrapper {
    overflow-x: auto;
  }

  .data-table {
    min-width: 1000px;
  }

  .pagination {
    flex-direction: column;
    padding: 16px;
    align-items: stretch;
  }

  .pagination-left {
    width: 100%;
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .items-per-page {
    width: 100%;
    justify-content: space-between;
  }

  .items-select {
    max-width: 100px;
  }

  .pagination-controls {
    width: 100%;
    justify-content: center;
    flex-wrap: wrap;
  }

  .produtos-pagination {
    flex-direction: column;
    gap: 12px;
  }

  .produtos-pagination-controls {
    width: 100%;
    justify-content: center;
    flex-wrap: wrap;
  }
}

@media (max-width: 600px) {
  .pagination {
    padding: 16px;
  }

  .page-btn {
    min-width: 36px !important;
  }

  .level-1-header th,
  .level-2-table thead th,
  .level-3-table thead th {
    font-size: 0.688rem;
    padding: 12px 8px;
  }

  .level-1-row td,
  .level-2-row td,
  .level-3-row td {
    padding: 10px 8px;
    font-size: 0.75rem;
  }
}

.table-wrapper::-webkit-scrollbar {
  height: 8px;
}

.table-wrapper::-webkit-scrollbar-track {
  background: #f3f4f6;
  border-radius: 4px;
}

.table-wrapper::-webkit-scrollbar-thumb {
  background: rgba(2, 52, 73, 0.3);
  border-radius: 4px;
}

.table-wrapper::-webkit-scrollbar-thumb:hover {
  background: rgba(2, 52, 73, 0.5);
}

@keyframes slideDown {
  from {
    opacity: 0;
    max-height: 0;
  }
  to {
    opacity: 1;
    max-height: 1000px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .level-1-row,
  .level-2-row,
  .level-3-row {
    transition: none !important;
  }
}
</style>